import React from "react";
import {
  Calendar,
  Settings,
  BarChart3,
  Clock,
  Database,
  Wifi,
  WifiOff,
  Activity,
} from "lucide-react";

interface HeaderProps {
  activeView: string;
  onViewChange: (view: string) => void;
  jiraConnected: boolean;
  useMockData: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  activeView,
  onViewChange,
  jiraConnected,
  useMockData,
}) => {
  const navItems = [
    { id: "timeline", label: "Timeline", icon: Calendar },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "worklog", label: "Time Tracking", icon: Clock },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-white/10 shadow-lg">
      {/* Subtle header gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-transparent" />

      <div className="relative px-4 sm:px-6 py-4">
        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between">
          {/* Logo and Title */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* Optimized app icon */}
            <div className="group relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl blur opacity-75 group-hover:opacity-100 transition duration-300" />
              <div className="relative w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-apple-card transform transition-transform group-hover:scale-105">
                <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
            </div>

            <div className="flex flex-col">
              <h1 className="apple-headline text-system-label text-sm sm:text-base">
                Jira Timeline
              </h1>
              <p className="apple-caption2 text-system-label-tertiary hidden sm:block">
                Project Management Suite
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <div className="glass apple-navigation p-1">
              {navItems.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => onViewChange(id)}
                  className={`apple-nav-item flex items-center space-x-2 px-4 py-2.5 rounded-apple transition-all duration-apple-fast group ${
                    activeView === id
                      ? "bg-system-label text-white shadow-apple-button-pressed"
                      : "text-system-label-secondary hover:text-system-label hover:bg-system-fill/10"
                  }`}
                >
                  <Icon className="w-4 h-4 transition-transform group-hover:scale-110" />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </div>
          </nav>

          {/* Status Indicators */}
          <div className="flex items-center space-x-3">
            {/* Connection Status */}
            <div className="glass apple-card-glass px-3 py-2 rounded-apple-xl">
              <div className="flex items-center space-x-2">
                {useMockData ? (
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <Database className="w-4 h-4 text-apple-orange" />
                      <div className="absolute inset-0 w-4 h-4 bg-apple-orange/20 rounded-full animate-apple-pulse" />
                    </div>
                    <span className="apple-caption2 text-system-label font-medium hidden xl:block">
                      Demo
                    </span>
                  </div>
                ) : jiraConnected ? (
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <Wifi className="w-4 h-4 text-apple-green" />
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-apple-green rounded-full animate-apple-pulse" />
                    </div>
                    <span className="apple-caption2 text-system-label font-medium hidden xl:block">
                      Connected
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <WifiOff className="w-4 h-4 text-apple-red" />
                    <span className="apple-caption2 text-system-label font-medium hidden xl:block">
                      Offline
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Sprint Info - Optimized */}
            <div className="hidden lg:block glass apple-card-glass px-3 py-2 rounded-apple-xl">
              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4 text-apple-blue" />
                <div className="text-right">
                  <p className="apple-caption2 text-system-label-tertiary">
                    Sprint
                  </p>
                  <p className="apple-caption2 text-system-label font-medium">
                    2
                  </p>
                </div>
              </div>
            </div>

            {/* User Avatar */}
            <div className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-300" />
              <div className="relative w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center cursor-pointer transform transition-transform group-hover:scale-105">
                <span className="text-white font-semibold text-xs sm:text-sm">JS</span>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4">
            {/* Logo and Title */}
            <div className="flex items-center space-x-3">
              <div className="relative w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-apple-card">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div className="flex flex-col">
                <h1 className="apple-headline text-system-label text-sm">
                  Jira Timeline
                </h1>
                <div className="flex items-center space-x-1 mt-1">
                  {useMockData ? (
                    <div className="flex items-center space-x-1">
                      <Database className="w-3 h-3 text-apple-orange" />
                      <span className="apple-caption2 text-system-label-tertiary">Demo</span>
                    </div>
                  ) : jiraConnected ? (
                    <div className="flex items-center space-x-1">
                      <Wifi className="w-3 h-3 text-apple-green" />
                      <span className="apple-caption2 text-system-label-tertiary">Connected</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <WifiOff className="w-3 h-3 text-apple-red" />
                      <span className="apple-caption2 text-system-label-tertiary">Offline</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* User Avatar */}
            <div className="relative w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold text-xs">JS</span>
            </div>
          </div>

          {/* Mobile Navigation */}
          <nav className="glass apple-navigation p-1">
            <div className="grid grid-cols-4 gap-1">
              {navItems.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => onViewChange(id)}
                  className={`flex flex-col items-center space-y-1 px-2 py-2 rounded-apple transition-all duration-apple-fast ${
                    activeView === id
                      ? "bg-system-label text-white"
                      : "text-system-label-secondary hover:text-system-label"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-medium leading-tight">{label}</span>
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Medium Screen Navigation (Tablet) */}
        <div className="hidden md:flex lg:hidden items-center justify-between mt-4 pt-3 border-t border-system-separator/30">
          <div className="flex-1" />
          <nav className="glass apple-navigation p-1">
            <div className="flex space-x-1">
              {navItems.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => onViewChange(id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-apple transition-all duration-apple-fast ${
                    activeView === id
                      ? "bg-system-label text-white shadow-apple-button-pressed"
                      : "text-system-label-secondary hover:text-system-label hover:bg-system-fill/10"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </div>
          </nav>
          <div className="flex-1 flex justify-end">
            <div className="glass apple-card-glass px-3 py-2 rounded-apple-xl">
              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4 text-apple-blue" />
                <span className="apple-caption2 text-system-label font-medium">Sprint 2</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-system-separator to-transparent opacity-50" />
    </header>
  );
};
